import { Component, Input, OnInit, OnChanges } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { PagePerformanceVersionService } from '@client/app/modules/page-performance/services/page-performance-version/page-performance-version.service';
import MonColors from '@monsido/colors/dist/es6/colors';

@Component({
    selector: 'page-performance-page-details-load-times-trend',
    standalone: false,
    template: `
        <div class="load-times-trend-container">
            <div *ngIf="history.length > 0">
                <div class="load-times-trend-header">
                    <div class="load-times-trend-title">
                        <div class="load-times-trend-title-text" translate>Load Times Trend</div>
                        <div class="load-times-trend-subtitle-text"
                             translate [translate-args]="{date: lastScan}">Measurements since [[date]]</div>
                    </div>
                    <div class="rulers-section-container d-flex flex-wrap">
                        <div class="ruler-container" *ngFor="let point of summary">
                            <span class="vertical-ruler vertical-ruler-left" [ngClass]="point.class"></span>
                            <div class="ruler-text-container">
                                <div class="ruler-text-label">
                                    {{point.label}}
                                </div>
                                <div class="ruler-text-value" translate [translate-args]="{avg: point.value}">[[avg]]</div>
                                <div *ngIf="report.v2 && point.weight" class="ruler-text-sub" translate
                                     [translate-args]="{weight: point.weight}">Weight in score: [[weight]]</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="load-times-trend-content">
                    <div class="load-times-trend-line-chart">
                        <mon-chart-line [height]="'300'"
                                    [data]="data"
                                    [labels]="labels"
                                    [series]="series"
                                    [colors]="colors"
                                    [override]="override"
                                    [options]="options">
                        </mon-chart-line>
                    </div>
                </div>
            </div>
            <div *ngIf="history.length === 0">
                <p translate>No performance scan found</p>
            </div>
        </div>
    `,
})
export class PagePerformancePageDetailsLoadTimesTrendComponent implements OnInit, OnChanges {
    @Input() history: any;
    @Input() report: any;

    versions: any = [];
    seriesByVersion: any = {};
    summary: any;
    data: any;
    labels: any;
    series: any;
    colors: any;
    override: any;
    options: any;
    metrics: any;
    metricsIds: any;
    lastScan: any;

    constructor (
        private translateService: TranslateService,
        private pipeService: PipeService,
        private dateTimeService: DateTimeService,
        private pagePerformanceVersionService: PagePerformanceVersionService,
    ) {}

    ngOnInit () {
        this.activate();
    }

    ngOnChanges () {
        this.setupChart();
        this.setupChartData();
    }

    activate () {
        this.versions = [];
        this.seriesByVersion = {};
        const versionInfo = this.pagePerformanceVersionService.getVersionInfo(this.report.version);
        const metricUseDisplayValue = ['cumulative-layout-shift'];
        this.summary = versionInfo?.metrics?.map((metric: any) => {
            const useDisplayValue = metricUseDisplayValue.indexOf(metric.auditId) > -1;
            return {
                id: metric.auditId,
                label: this.translateService.getString(metric.label),
                value:
                    this.pipeService.get('monNumber')(this.report.audits[metric.auditId][useDisplayValue ? 'displayValue' : 'numericValue']) +
                    ' ' +
                    metric.unit,
                class: metric.rulerClass,
                weight: '',
            };
        });
        this.history = this.history || [];
        this.setWeights();
        this.setupChart();
        this.setupChartData();
    }

    setWeights () {
        if (
            !this.summary ||
            !this.report ||
            !this.report.categories ||
            !this.report.categories.performance ||
            !this.report.categories.performance.auditRefs
        ) {
            return;
        }
        for (let i = 0; i < this.summary.length; i++) {
            const metric = this.summary[i];
            const auditRef = this.report.categories.performance.auditRefs.find((ref: any) => {
                return ref.id === metric.id;
            });
            if (auditRef) {
                metric.weight = auditRef.weight + '%';
            }
        }
    }

    setupChart () {
        const availableColors = [
            '#f4151e',
            '#6122ee',
            MonColors['secondary-12'],
            '#00DEDB',
            '#FF9879',
            MonColors['secondary-15'],
            MonColors['secondary-11'],
            MonColors['secondary-16'],
        ];
        const availableOverrides = [
            {
                xAxisID: 'x-axis-0',
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                pointHitRadius: 10,
                backgroundColor: '#ffffff',
                borderDash: [6, 6],
                yAxisID: '',
            },
            {
                xAxisID: 'x-axis-0',
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                pointHitRadius: 10,
                backgroundColor: '#ffffff',
                yAxisID: '',
            },
            {
                xAxisID: 'x-axis-0',
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                pointHitRadius: 10,
                borderDash: [4, 4],
                yAxisID: '',
            },
            {
                xAxisID: 'x-axis-0',
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                pointHitRadius: 10,
                yAxisID: '',
            },
            {
                xAxisID: 'x-axis-0',
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                pointHitRadius: 10,
                borderDash: [2, 2],
                yAxisID: '',
            },
            {
                xAxisID: 'x-axis-0',
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                yAxisID: '',
            },
        ];

        const seriesByVersion = this.pagePerformanceVersionService.getSeriesByVersion(this.history, 'version');
        this.versions = seriesByVersion.versions;
        this.seriesByVersion = seriesByVersion.series;

        if (this.versions.length === 1) {
            this.metrics = this.versions[0].metrics;
        } else {
            this.metrics = [];
            this.metricsIds = {};
            for (let i = 0; i < this.versions.length; i++) {
                const versionMetrics = this.versions[i].metrics;
                for (let j = 0; j < versionMetrics.length; j++) {
                    const versionMetric = versionMetrics[j];
                    if (!this.metricsIds[versionMetric.id]) {
                        this.metrics.push(versionMetric);
                        this.metricsIds[versionMetric.id] = versionMetric;
                    }
                }
            }
        }

        this.series = [];
        this.colors = [];
        this.override = [];

        this.metrics.forEach((metric: any, index: number) => {
            this.series.push(metric.chartLabel);
            this.colors.push(availableColors[index]);
            let override = availableOverrides[index];
            if (!override) {
                override = availableOverrides[index - availableOverrides.length];
            }
            override.yAxisID = this.getYAxis(metric.unit);
            this.override.push(override);
        });
        this.options = {
            layout: {
                padding: {
                    top: 5,
                    right: 30,
                },
            },
            scaleShowValues: true,
            tooltips: {
                callbacks: {
                    label: (tooltipItem: any, data: any) => {
                        return (
                            data.datasets[tooltipItem.datasetIndex].label +
                            ': ' +
                            this.pipeService.get('monNumber')(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] / 1000, '0.[00]') +
                            (tooltipItem.datasetIndex === 5 ? '' : 's')
                        );
                    },
                },
            },
            scales: {
                xAxes: [
                    {
                        id: 'x-axis-0',
                        type: 'time',
                        gridLines: {
                            offsetGridLines: true,
                            drawBorder: false,
                            display: false,
                        },
                        ticks: {
                            fontSize: 11,
                            maxTicksLimit: 30,
                        },
                        time: {
                            minUnit: 'day',
                            displayFormats: {
                                millisecond: 'MMM D',
                                second: 'MMM D',
                                minute: 'MMM D',
                                hour: 'MMM D',
                                day: 'MMM D',
                                week: 'MMM D',
                                month: 'MMM D',
                                quarter: 'MMM D',
                                year: 'MMM D',
                            },
                        },
                    },
                ],
                yAxes: [
                    {
                        id: 'y-axis-1',
                        type: 'linear',
                        position: 'left',
                        ticks: {
                            fontSize: 11,
                            min: 0,
                            beginAtZero: true,
                            suggestedMax: 5,
                            maxTicksLimit: 5,
                            callback: (value: any) => {
                                return this.pipeService.get('monNumber')(value, '0,0');
                            },
                        },
                        scaleLabel: {
                            display: true,
                            labelString: this.translateService.getString('Milliseconds'),
                            fontSize: 11,
                        },
                    },
                    {
                        id: 'y-axis-2',
                        type: 'linear',
                        position: 'right',
                        ticks: {
                            fontSize: 11,
                            min: 0,
                            max: 1,
                            beginAtZero: true,
                            suggestedMax: 1,
                            maxTicksLimit: 2,
                            callback: (value: any) => {
                                return this.pipeService.get('monNumber')(value, '0,00');
                            },
                        },
                        gridLines: {
                            drawBorder: false,
                            display: false,
                        },
                    },
                ],
            },
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    boxWidth: 13,
                },
            },
        };

        const entriesWithVersionChanged = this.pagePerformanceVersionService.getEntriesWithVersionChanged(this.history, 'version');
        if (entriesWithVersionChanged.length > 0) {
            this.options.verticalLines = {
                lines: entriesWithVersionChanged.map((entry: any) => {
                    const versionInfo = this.pagePerformanceVersionService.getVersionInfo(entry.version);
                    return {
                        xAxisID: 'x-axis-0',
                        x: entry.scanned_at,
                        style: {
                            lineDash: [5, 5],
                            color: MonColors['secondary-15'],
                            width: 3,
                        },
                        label: {
                            text: (versionInfo as any)?.label || '',
                            color: MonColors['secondary-15'],
                            position: 'top',
                        },
                        data: {
                            link: (versionInfo as any)?.link || '',
                        },
                    };
                }),
                timeScale: this.history.map((entry: any) => entry.scanned_at),
            };
        }
    }

    getYAxis (unit: string) {
        switch (unit) {
            case 'ms':
                return 'y-axis-1';
            default:
                return 'y-axis-2';
        }
    }

    setupChartData () {
        if (!Array.isArray(this.history) || this.history.length === 0) {
            return;
        }
        this.data = this.metrics.map(() => {
            return [];
        });
        this.labels = [];
        this.lastScan = this.dateTimeService.formatBrowserLocalized(this.history[0].scanned_at, 'LL LTS');
        this.history.forEach((entry: any) => {
            this.labels.push(this.dateTimeService.formatBrowserLocalized(entry.scanned_at, 'll LT'));
            this.metrics.forEach((metric: any, index: number) => {
                this.data[index].push(parseFloat(entry[metric.id]));
            });
        });
    }
}
